import React from 'react'

import { Box } from 'ui/components/Box'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'ui/components/Collapsible'
import { Icon } from 'ui/components/Icon'
import { Body } from 'ui/components/Text'
import { stopPropagation } from 'ui/helpers/utilities'

import { useNavigationSectionState } from './hooks/useNavigationSectionState'

import {
    WorkspaceNavigationSectionHoverStyle,
    WorkspaceNavigationSectionLabelStyle,
    WorkspaceNavigationSectionStyle,
} from './Navigation.css'

type NavigationSectionProps = React.ComponentPropsWithoutRef<typeof Box> & {
    stateStorageKey: string
    name: string
    buttonProps?: Partial<React.ComponentPropsWithoutRef<typeof Box>>
    hideButton?: boolean
    rightSlotContent?: React.ReactNode
}

export const NavigationSection: React.FC<NavigationSectionProps> = ({
    stateStorageKey,
    name,
    buttonProps,
    hideButton,
    children,
    rightSlotContent,
    ...props
}) => {
    const { isCollapsed, toggleIsCollapsed } = useNavigationSectionState({
        stateStorageKey,
    })

    if (!children) return null

    return (
        <Box as={Collapsible} open={!isCollapsed} onOpenChange={toggleIsCollapsed} trim {...props}>
            {!hideButton && (
                <CollapsibleTrigger asChild>
                    <Box
                        role="button"
                        py="2xs"
                        className={WorkspaceNavigationSectionStyle}
                        {...buttonProps}
                    >
                        <Box className={WorkspaceNavigationSectionLabelStyle}>
                            <Icon
                                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                                size="s"
                                p="3xs"
                                family="hugeicons"
                                name={isCollapsed ? 'arrow-right-01' : 'arrow-down-01'}
                                color="icon"
                            />
                            <Body size="s" weight="medium" color="textWeaker" trim px="2xs">
                                {name}
                            </Body>
                        </Box>
                        {rightSlotContent && (
                            <Box
                                className={WorkspaceNavigationSectionHoverStyle}
                                onClick={stopPropagation}
                                noShrink
                            >
                                {rightSlotContent}
                            </Box>
                        )}
                    </Box>
                </CollapsibleTrigger>
            )}
            <CollapsibleContent>{children}</CollapsibleContent>
        </Box>
    )
}
